<script lang="ts">
import { Vue, Component } from 'vue-facing-decorator';
import { useRouter } from 'vue-router';
import { usePostHog } from '@/composables/usePostHog';

@Component
export default class HeroSection extends Vue {
  private router = useRouter();
  private posthog = usePostHog().posthog;

  private navigateToProjects() {
    this.posthog.capture('hero_cta_clicked', { target: 'projects', type: 'secondary' });
    this.router.push({ name: 'projects' });
  }

  private navigateToInfra() {
    this.posthog.capture('hero_cta_clicked', { target: 'infrastructure', type: 'primary' });
    this.router.push({ name: 'infrastructure' });
  }
}
</script>

<template>
  <section class="hero-container">
    <div class="hero-content">
      <!-- Status Badges -->
      <div class="status-badges">
        <div class="badge status-dev">
          <span class="mdi mdi-code-braces"></span>
          {{ $t('home.hero.badgeStatus') }}
        </div>
        <div class="badge focus-cka">
          <span class="mdi mdi-certificate"></span>
          {{ $t('home.hero.badgeFocus') }}
        </div>
      </div>

      <!-- Main Text -->
      <h1 class="hero-title">
        <span class="name">{{ $t('home.title') }}</span>
        <span class="subtitle">{{ $t('home.subtitle') }}</span>
      </h1>

      <p class="hero-pitch" v-html="$t('home.hero.pitch')"></p>

      <!-- CTAs -->
      <div class="hero-actions">
        <button class="cta-primary" @click="navigateToInfra">
          <span class="mdi mdi-cloud-sync"></span>
          {{ $t('home.hero.ctaInfra') }}
        </button>
        <button class="cta-secondary" @click="navigateToProjects">
          <span class="mdi mdi-view-grid-outline"></span>
          {{ $t('home.hero.ctaProjects') }}
        </button>
      </div>
    </div>

    <!-- Right Side: Photo with Glass Frame -->
    <div class="hero-visual">
      <div class="photo-frame">
        <img src="/PP.png" alt="Raphaël Girardy" class="profile-photo" />
        <div class="glass-overlay"></div>
      </div>
    </div>

    <!-- Bottom Pillars -->
    <div class="hero-pillars">
      <div class="pillar">
        <span class="mdi mdi-pillar"></span>
        <div class="pillar-text">
          <h3>{{ $t('home.hero.pillarArch') }}</h3>
          <p>{{ $t('home.hero.pillarArchDesc') }}</p>
        </div>
      </div>
      <div class="pillar">
        <span class="mdi mdi-kubernetes"></span>
        <div class="pillar-text">
          <h3>{{ $t('home.hero.pillarOrch') }}</h3>
          <p>{{ $t('home.hero.pillarOrchDesc') }}</p>
        </div>
      </div>
      <div class="pillar">
        <span class="mdi mdi-shield-check-outline"></span>
        <div class="pillar-text">
          <h3>{{ $t('home.hero.pillarSec') }}</h3>
          <p>{{ $t('home.hero.pillarSecDesc') }}</p>
        </div>
      </div>
    </div>
  </section>
</template>

<style scoped>
.hero-container {
  display: grid;
  grid-template-columns: 1.2fr 0.8fr;
  min-height: calc(100vh - var(--ln-nav-offset));
  padding: 4rem 6rem;
  gap: 4rem;
  align-items: center;
  position: relative;
  overflow: hidden;
  background: transparent;
  max-width: var(--ln-content-max-width);
  margin: 0 auto;
}

.hero-content {
  display: flex;
  flex-direction: column;
  gap: 2rem;
  z-index: 2;
  animation: glassAppear 1s var(--ln-ease-smooth);
  text-align: left;
}

.status-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  border-radius: 2rem;
  font-size: 0.85rem;
  font-weight: 600;
  background: var(--ln-glass-surface);
  border: 1px solid var(--ln-glass-border);
  backdrop-filter: blur(8px);
  color: var(--ln-text-primary);
}

.status-dev span.mdi {
  color: var(--ln-teal-deep);
  font-size: 1.1rem;
}

.focus-cka span.mdi {
  color: var(--ln-terracotta-subtle);
  font-size: 1.1rem;
}

.hero-title {
  display: flex;
  flex-direction: column;
}

.name {
  font-size: clamp(3rem, 6vw, 4.5rem);
  font-weight: 800;
  background: linear-gradient(135deg, var(--ln-text-primary) 30%, var(--ln-teal-deep));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  line-height: 1.1;
  margin-bottom: 0.5rem;
}

.subtitle {
  font-size: clamp(1.4rem, 3vw, 1.8rem);
  color: var(--ln-stone-grey);
  font-weight: 600;
  line-height: 1.3;
}

.hero-pitch {
  font-size: 1.15rem;
  max-width: 650px;
  color: var(--ln-text-secondary);
  line-height: 1.8;
}

.hero-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 1.5rem;
  margin-top: 1rem;
}

button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  padding: 1rem 2rem;
  border-radius: var(--ln-radius-md);
  font-weight: 700;
  cursor: pointer;
  transition: all var(--ln-transition-normal) var(--ln-ease-bounce);
  font-size: 1rem;
}

.cta-primary {
  background: var(--ln-teal-deep);
  color: white;
  border: none;
  box-shadow: 0 8px 24px rgba(44, 122, 123, 0.3);
}

.cta-primary:hover {
  background: var(--ln-teal-light);
  transform: translateY(-5px) scale(1.02);
  box-shadow: 0 12px 32px rgba(44, 122, 123, 0.4);
}

.cta-secondary {
  background: var(--ln-glass-surface);
  color: var(--ln-text-primary);
  border: 1px solid var(--ln-glass-border);
  backdrop-filter: blur(12px);
}

.cta-secondary:hover {
  background: var(--ln-glass-elevated);
  transform: translateY(-5px) scale(1.02);
  border-color: var(--ln-teal-deep);
}

.hero-visual {
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
}

.photo-frame {
  position: relative;
  width: 100%;
  max-width: 380px;
  aspect-ratio: 3 / 4;
  border-radius: var(--ln-radius-lg);
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(58, 52, 46, 0.15); /* More visible shadow but dark */
  animation: glassFloat 6s ease-in-out infinite;
  border: 1px solid var(--ln-glass-border-strong);
}

[data-theme="dark"] .photo-frame {
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.profile-photo {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform var(--ln-transition-slow) var(--ln-ease-smooth);
}

.photo-frame:hover .profile-photo {
  transform: scale(1.05);
}

.glass-overlay {
  position: absolute;
  inset: 0;
  background: linear-gradient(to bottom, transparent 60%, rgba(42, 36, 32, 0.4)); /* Darker overlay for contrast */
  pointer-events: none;
}

.hero-pillars {
  grid-column: 1 / span 2;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 4rem;
  animation: glassFadeIn 1.2s var(--ln-ease-smooth);
}

.pillar {
  display: flex;
  align-items: flex-start;
  gap: 1.5rem;
  padding: 2rem;
  background: var(--ln-glass-surface);
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border);
  transition: all var(--ln-transition-normal) var(--ln-ease-smooth);
}

.pillar:hover {
  background: var(--ln-glass-elevated);
  border-color: var(--ln-teal-deep);
  transform: translateY(-8px);
}

.pillar span.mdi {
  font-size: 2.5rem;
  color: var(--ln-teal-deep);
}

.pillar-text h3 {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
  letter-spacing: 1px;
}

.pillar-text p {
  font-size: 0.9rem;
  color: var(--ln-text-muted);
}

/* Tablet & Mobile Responsive */
@media (max-width: 1024px) {
  .hero-container {
    margin-top: 2rem;
    padding: 6rem 2rem 2rem 2rem;
    display: flex;
    flex-direction: column;
    text-align: center;
    gap: 3rem;
  }
  
  .hero-content {
    align-items: center;
    text-align: center;
    gap: 1.5rem;
  }
  
  .status-badges {
    justify-content: center;
  }

  .hero-pitch {
    font-size: 1.05rem;
  }

  .hero-visual {
    order: -1; /* Mettre la photo en haut sur mobile */
    margin-bottom: 1rem;
  }

  .photo-frame {
    max-width: 250px;
    height: auto;
    animation: glassFloatMobile 6s ease-in-out infinite;
  }

  .hero-actions {
    flex-direction: column;
    width: 100%;
    max-width: 400px;
  }
  
  .hero-actions button {
    width: 100%;
  }

  .hero-pillars {
    grid-template-columns: 1fr;
    margin-top: 1rem;
    width: 100%;
  }
  
  .pillar {
    padding: 1.5rem;
  }
}

@keyframes glassFloatMobile {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-10px); }
}
</style>
